import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../services/api';
import Pagination from '../components/shared/Pagination';

export default function FinanceCompaniesIndex() {
    const location = useLocation();
    const [companies, setCompanies] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ 
        total_pages: 1, 
        total_count: 0, 
        current_page: 1,
        per_page: 25 
    });

    useEffect(() => {
        loadCompanies();
    }, [location, currentPage, searchTerm]);

    const loadCompanies = async () => {
        try {
            setLoading(true);
            const params: any = { 
                page: currentPage,
                q: searchTerm
            };
            const response = await api.get('/finance_companies', { params });
            if (response.success) {
                const companiesData = response.data.data || response.data || [];
                setCompanies(companiesData);
                if (response.meta) {
                    setPagination({
                        total_pages: response.meta.total_pages || 1,
                        total_count: response.meta.total_count || 0,
                        current_page: response.meta.current_page || 1,
                        per_page: response.meta.per_page || 25
                    });
                }
            }
        } catch (error) {
            console.error('Error loading finance companies:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this finance company?')) return;

        try {
            await api.delete(`/finance_companies/${id}`);
            loadCompanies();
        } catch (error) {
            alert('Error deleting company. It may be in use.');
        }
    };

    if (loading && companies.length === 0) return <div className="p-8 text-center text-slate-500 font-medium italic animate-pulse">Synchronizing Finance Data...</div>;

    return (
        <div className="p-8 max-w-7xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-6">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tight">Finance <span className="text-indigo-600">Companies</span></h1>
                    <p className="text-slate-500 mt-2 font-medium italic">Manage banks and NBFCs ({pagination.total_count} identified)</p>
                </div>
                <Link
                    to="/finance_companies/new"
                    className="group relative inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] transition-all hover:bg-black hover:scale-105 active:scale-95 shadow-2xl shadow-indigo-100"
                >
                    <span className="relative z-10 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" />
                        </svg>
                        Add Company
                    </span>
                </Link>
            </div>

            {/* Search */}
            <div className="relative group mb-10 max-w-md">
                <input
                    type="text"
                    placeholder="Search by company name..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="w-full px-6 py-4 bg-white border border-slate-100 rounded-2xl shadow-sm focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-medium text-slate-600 group-hover:shadow-md"
                />
                <div className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-300">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                </div>
            </div>

            <div className="bg-white rounded-3xl border border-slate-100 shadow-xl overflow-hidden mb-12">
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-left">
                        <thead>
                            <tr className="bg-slate-50/50">
                                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100">Company Identity</th>
                                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100">Classification</th>
                                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100">Contact Details</th>
                                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100 text-center">Status</th>
                                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100 text-right">Operations</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {companies.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-8 py-20 text-center">
                                        <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-200">
                                            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>
                                        </div>
                                        <div className="text-xl font-bold text-slate-900 mb-1">Company List Depleted</div>
                                        <p className="text-slate-400 font-medium italic">No companies matching your search were identified.</p>
                                    </td>
                                </tr>
                            ) : (
                                companies.map((company: any) => (
                                    <tr key={company.id} className="group hover:bg-slate-50/50 transition-colors">
                                        <td className="px-8 py-6">
                                            <Link to={`/finance_companies/${company.id}`} className="font-black text-slate-900 group-hover:text-indigo-600 transition-colors">
                                                {company.company_name}
                                            </Link>
                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">ID: #{company.id.toString().padStart(4, '0')}</div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${company.company_type === 'bank' ? 'bg-blue-50 text-blue-600' :
                                                company.company_type === 'nbfc' ? 'bg-purple-50 text-purple-600' :
                                                    'bg-slate-50 text-slate-500'
                                                }`}>
                                                {company.company_type?.toUpperCase() || 'OTHER'}
                                            </span>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="text-sm font-bold text-slate-700">{company.contact_person || 'N/A'}</div>
                                            <div className="text-[10px] font-bold text-slate-400 mt-1">{company.phone || company.email || 'No contact provided'}</div>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${company.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
                                                }`}>
                                                {company.is_active ? 'Active' : 'Deactivated'}
                                            </span>
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <div className="flex items-center justify-end gap-2 outline-none">
                                                <Link
                                                    to={`/finance_companies/${company.id}/edit`}
                                                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 text-slate-400 hover:bg-slate-900 hover:text-white transition-all transform hover:scale-110 active:scale-95"
                                                    title="Edit Company"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                                </Link>
                                                <button
                                                    onClick={() => handleDelete(company.id)}
                                                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 text-slate-400 hover:bg-rose-600 hover:text-white transition-all transform hover:scale-110 active:scale-95"
                                                    title="Delete Company"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {companies.length > 0 && (
                <Pagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.total_pages}
                    totalCount={pagination.total_count}
                    perPage={pagination.per_page}
                    onPageChange={handlePageChange}
                />
            )}
        </div>
    );
}
